/**
 * PushNotificationToggle Component
 * 
 * Allows users to enable/disable browser push notifications
 */
import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { toast } from 'sonner';
import { Bell, BellOff, AlertCircle, Loader2, CheckCircle2 } from 'lucide-react';
import api from '../lib/api';
import {
  isPushSupported,
  getPermissionStatus,
  registerServiceWorker,
  requestNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribed as checkIsSubscribed
} from '../lib/pushNotifications';

export default function PushNotificationToggle() {
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    checkPushStatus();
  }, []);

  const checkPushStatus = async () => {
    setLoading(true);
    try {
      if (!isPushSupported()) {
        setSupported(false);
        setLoading(false);
        return;
      }

      await registerServiceWorker();
      setPermission(getPermissionStatus());
      const sub = await checkIsSubscribed();
      setSubscribed(sub);
    } catch (error) {
      console.error('Error checking push status:', error);
    }
    setLoading(false);
  };

  const handleToggle = async (enabled) => {
    setActionLoading(true);
    try {
      if (enabled) {
        // Request permission first
        const perm = await requestNotificationPermission();
        setPermission(perm);

        if (perm !== 'granted') {
          toast.error('Push-Benachrichtigungen wurden blockiert. Bitte erlauben Sie diese in Ihren Browser-Einstellungen.');
          setActionLoading(false);
          return;
        }

        // Subscribe
        await subscribeToPush();
        setSubscribed(true);
        toast.success('Push-Benachrichtigungen aktiviert');
      } else {
        // Unsubscribe
        await unsubscribeFromPush();
        setSubscribed(false);
        toast.success('Push-Benachrichtigungen deaktiviert');
      }
    } catch (error) {
      console.error('Push toggle error:', error);
      toast.error(error?.response?.data?.detail || error.message || 'Fehler beim Ändern der Push-Einstellungen');
    }
    setActionLoading(false);
  };

  const handleTestNotification = async () => {
    setActionLoading(true);
    try {
      await api.post('/push/test');
      toast.success('Test-Benachrichtigung wurde gesendet — schau auf das App-Icon / die Banner-Anzeige.');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Test-Benachrichtigung fehlgeschlagen');
    }
    setActionLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Lade Push-Einstellungen...</span>
      </div>
    );
  }

  if (!supported) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Ihr Browser unterstützt keine Push-Benachrichtigungen.
        </AlertDescription>
      </Alert>
    );
  }

  if (permission === 'denied') {
    return (
      <Alert>
        <BellOff className="h-4 w-4" />
        <AlertDescription>
          Push-Benachrichtigungen wurden blockiert. Bitte erlauben Sie diese in Ihren Browser-Einstellungen und laden Sie die Seite neu.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {subscribed ? (
            <div className="p-2 rounded-lg bg-green-100 text-green-600">
              <Bell className="h-5 w-5" />
            </div>
          ) : (
            <div className="p-2 rounded-lg bg-muted text-muted-foreground">
              <BellOff className="h-5 w-5" />
            </div>
          )}
          <div>
            <Label className="text-base font-medium">Browser Push-Benachrichtigungen</Label>
            <p className="text-sm text-muted-foreground">
              Erhalten Sie Benachrichtigungen auch wenn die App geschlossen ist
            </p>
          </div>
        </div>
        <Switch
          checked={subscribed}
          onCheckedChange={handleToggle}
          disabled={actionLoading}
          data-testid="push-notification-toggle"
        />
      </div>

      {subscribed && (
        <div className="flex flex-col gap-2 pl-12 sm:flex-row sm:items-center">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span className="text-sm text-green-600">Push-Benachrichtigungen sind aktiv</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleTestNotification}
            disabled={actionLoading}
            data-testid="test-push-btn"
            className="sm:ml-auto"
          >
            {actionLoading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Bell className="h-4 w-4 mr-2" />
            )}
            Jetzt testen
          </Button>
        </div>
      )}

      {subscribed && (
        <p className="text-xs text-muted-foreground pl-12">
          Beim Klick auf „Jetzt testen" wird eine Test-Benachrichtigung an dieses Gerät gesendet. Du solltest sofort ein OS-Banner sehen und – falls die App als PWA installiert ist – eine Zahl auf dem App-Icon.
        </p>
      )}
    </div>
  );
}
